import React, { useState, useMemo, useRef } from 'react';
import { textures as defaultTextures } from '../../data/products';
import { useAdmin } from '../../context/AdminContext';
import { useCart } from '../../context/CartContext';
import './ProductCard.css';

const ProductCard = ({ product, onProductClick, onAddToCart }) => {
  const { addToCart: contextAddToCart } = useCart();
  const { textures: adminTextures } = useAdmin();
  const isAddingRef = useRef(false);
  const [shareCopied, setShareCopied] = useState(false);

  const textures = useMemo(() => {
    if (Array.isArray(adminTextures) && adminTextures.length > 0) {
      const texturesObj = {};
      adminTextures.forEach(texture => {
        texturesObj[texture.id] = { name: texture.name, color: texture.color };
      });
      return { ...defaultTextures, ...texturesObj };
    }
    return defaultTextures;
  }, [adminTextures]);

  const hasTextures = product.textures && product.textures.length > 0;
  const [selectedTexture, setSelectedTexture] = useState(hasTextures ? product.textures[0] : null);

  const handleTextureChange = (e, texture) => {
    e.stopPropagation();
    setSelectedTexture(texture);
  };

  const handleAddToCartClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isAddingRef.current) {
      return;
    }

    try {
      const textureToAdd = hasTextures ? selectedTexture : null;
      if (hasTextures && !selectedTexture) {
        onProductClick(product, null);
        return;
      }

      isAddingRef.current = true;

      if (onAddToCart) {
        onAddToCart(product, textureToAdd);
      } else {
        contextAddToCart(product, textureToAdd);
      }

      setTimeout(() => {
        isAddingRef.current = false;
      }, 300);
    } catch (error) {
      isAddingRef.current = false;
      onProductClick(product, selectedTexture || null);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/shop/${product.id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const currentImage = (selectedTexture && product.images?.[selectedTexture]) || product.images?.default || '';
  const basePrice = parseFloat(product.price) || 0;
  const discountedPrice = product.discountedPrice ?? product.discounted_price;
  const hasDiscount = discountedPrice !== null && discountedPrice !== undefined && discountedPrice !== '';
  const effectivePrice = hasDiscount ? (parseFloat(discountedPrice) || 0) : basePrice;

  return (
    <div
      className="product-card"
      onClick={() => onProductClick(product, selectedTexture || null)}
    >
      <div className="product-image-container">
        <img
          src={currentImage}
          alt={product.name}
          className="product-image"
        />
        <div className="product-badges">
          {product.hidden && <div className="product-badge hidden">Hidden</div>}
          {product.hotRank && <div className="product-badge hot">Hot #{product.hotRank}</div>}
          {product.newArrival && <div className="product-badge new">New</div>}
          {product.featured && <div className="product-badge featured">Featured</div>}
          {hasDiscount && <div className="product-badge discount">Discount</div>}
        </div>
        <button
          className={`product-share-btn${shareCopied ? ' copied' : ''}`}
          onClick={handleShare}
          title={shareCopied ? 'Link copied!' : 'Share this item'}
          aria-label="Share product"
        >
          {shareCopied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          )}
        </button>
      </div>
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <p className={`product-additional-info${product.additionalInfo ? '' : ' empty'}`}>
          {product.additionalInfo || '\u00a0'}
        </p>

        <div className="product-price-wrap">
          {hasDiscount && <p className="product-price-old">${basePrice.toFixed(2)}</p>}
          <p className="product-price">${effectivePrice.toFixed(2)}</p>
        </div>
        {hasTextures && (
          <div className="product-textures" onClick={(e) => e.stopPropagation()}>
            {product.textures.map((texture) => {
              const textureData = textures[texture];
              if (!textureData) return null;
              return (
                <button
                  key={texture}
                  className={`texture-button ${selectedTexture === texture ? 'active' : ''}`}
                  onClick={(e) => handleTextureChange(e, texture)}
                  style={{ backgroundColor: textureData.color }}
                  aria-label={`Select ${textureData.name} color`}
                />
              );
            })}
          </div>
        )}
        {product.bundleEnabled && (
          <button
            type="button"
            className="product-bundle-link"
            onClick={(event) => {
              event.stopPropagation();
              onProductClick(product, selectedTexture || null);
            }}
          >
            Pick {Number(product.bundleQuantity) || 3} Color / Style options · {parseFloat(product.bundleDiscountPercent) || 0}% off
          </button>
        )}
        <button
          className="product-add-to-cart-button"
          onClick={handleAddToCartClick}
          aria-label="Add to cart"
        >
          Add to Cart
        </button>

      </div>

    </div>

  );
};

export default ProductCard;

